import { SensorEvent } from '../models/sensorEvent';
import { SensorValue } from '../models/sensorValue';
import { SensorLocationType, SensorType, SensorValueType } from '../models/constant/sensor';
import { LocationPosition } from '../models/locationGridMapping';
import { severityCss } from './severity';

const PRESSURE_TYPES = new Set<string>([
  SensorValueType.Pressure,
  SensorValueType.PressureTemperatureCompensated,
]);

// SensorBatteryVoltage is the sensor's own housekeeping telemetry, not a battery
// sensor's reading — excluded so a temp/device sensor isn't mis-iconed as battery.
const BATTERY_VALUE_TYPES = new Set<string>([
  SensorValueType.BatteryVoltage,
  SensorValueType.BatteryCharge,
  SensorValueType.BatteryStateOfCharge,
  SensorValueType.BatteryHealth,
  SensorValueType.BatteryAutonomy,
]);

const DEVICE_VALUE_TYPES = new Set<string>([
  SensorValueType.RfPerformanceNoise,
  SensorValueType.RfPerformanceRssi,
  SensorValueType.DigitalPgn,
  SensorValueType.DigitalData,
  SensorValueType.AnalogVoltageSensor1,
  SensorValueType.AnalogVoltageSensor2,
]);

const ENGINE_VALUE_TYPES = new Set<string>([
  SensorValueType.Temperature,
  SensorValueType.Viscosity,
  SensorValueType.DielectricConstant,
  SensorValueType.Density,
  SensorValueType.RUL,
  SensorValueType.ChangeInViscosityPercLastNormal,
  SensorValueType.ChangeInDielectricConstantPercLastNormal,
  SensorValueType.ChangeInDensityPercLastNormal,
  SensorValueType.PolarizationResistance,
  SensorValueType.TotalBaseNumber,
  SensorValueType.Depth,
  // Reefer zone readings share the thermometer glyph.
  SensorValueType.SetPoint,
  SensorValueType.ReturnAirTemperature,
  SensorValueType.DischargeAirTemperature,
]);

const FUEL_VALUE_TYPES = new Set<string>([
  SensorValueType.FuelLevel,
  SensorValueType.FuelTankSize,
]);

export const PART_TYPE_KEYS: Partial<Record<SensorLocationType, string>> = {
  [SensorLocationType.Tire]: '@partTypeTire',
  [SensorLocationType.SteerTire]: '@partTypeSteerTire',
  [SensorLocationType.Axle]: '@partTypeAxle',
  [SensorLocationType.Driveshaft]: '@partTypeDriveShaft',
  [SensorLocationType.Engine]: '@partTypeEngine',
  [SensorLocationType.Differential]: '@partTypeDifferential',
  [SensorLocationType.Battery]: '@partTypeBattery',
  [SensorLocationType.Device]: '@partTypeDevice',
};

export function pressureSensorValue(sensor: SensorEvent): SensorValue | null {
  return (sensor.values ?? []).find(v => PRESSURE_TYPES.has(v.sensorValueType)) ?? null;
}

// Unlike severityCss, a normal sensor reading (severity 0) is green (sev-ok);
// only unevaluated (< 0) stays grey.
export function sensorSeverityValueCss(severity: number): string {
  if (severity < 0) return 'sev-none';
  if (severity < 1) return 'sev-ok';
  return severityCss(severity);
}

export function sensorSeverityCss(sensor: SensorEvent): string {
  return sensor.values?.length ? sensorSeverityValueCss(sensor.overallSeverityValue) : 'sev-none';
}

export function isSensorStale(sensor: SensorEvent): boolean {
  return (sensor.values ?? []).find(v => v.isOverall)?.sensorValueType === SensorValueType.Staleness;
}

// layout grid cells store either the SensorLocationType value ('Steer Tire') or
// its key ('SteerTire', for spaced names) — accept both, default to Null.
export function normalizeLocationType(raw: string | undefined | null): SensorLocationType {
  if (!raw) return SensorLocationType.Null;
  const values = Object.values(SensorLocationType) as string[];
  if (values.includes(raw)) return raw as SensorLocationType;
  const keyIndex = Object.keys(SensorLocationType).indexOf(raw);
  return keyIndex >= 0 ? (values[keyIndex] as SensorLocationType) : SensorLocationType.Null;
}

function iconForLocationType(type: SensorLocationType): string {
  switch (type) {
    case SensorLocationType.Engine: return 'device_thermostat';
    case SensorLocationType.Battery: return 'battery_charging_full';
    case SensorLocationType.Device: return 'router';
    case SensorLocationType.Axle:
    case SensorLocationType.Driveshaft:
    case SensorLocationType.Differential: return 'settings';
    default: return 'sensors';
  }
}

// Glyph keys off the sensor's own value types (stable across trucks), falling
// back to the layout cell type only when no value type is recognised.
export function sensorIconFor(sensor: SensorEvent, locationType: SensorLocationType = SensorLocationType.Null): string {
  const types = (sensor.values ?? []).filter(v => !v.isOverall).map(v => v.sensorValueType);
  if (types.some(t => BATTERY_VALUE_TYPES.has(t))) return 'battery_charging_full';
  if (types.some(t => DEVICE_VALUE_TYPES.has(t))) return 'router';
  if (types.some(t => FUEL_VALUE_TYPES.has(t))) return 'local_gas_station';
  if (types.some(t => ENGINE_VALUE_TYPES.has(t))) return 'device_thermostat';
  return iconForLocationType(locationType);
}

export function isTireLocationType(type: SensorLocationType | undefined): boolean {
  return type === SensorLocationType.Tire || type === SensorLocationType.SteerTire;
}

// Bookkeeping sensor types carry no reading a person can act on.
const HIDDEN_SENSOR_TYPE_IDS = new Set<number>([
  SensorType.Null,
  SensorType.Geofence,
  SensorType.Dvir,
  SensorType.ScheduleVirtualSensor,
]);

export function displayableSensors(sensors: SensorEvent[] | null | undefined): SensorEvent[] {
  return (sensors ?? []).filter(s => !HIDDEN_SENSOR_TYPE_IDS.has(s.sensorTypeId));
}

export interface VehicleSensorLayout {
  grid?: Record<string, { type?: string }>;
}

// Per-vehicle layout resolves the grid-key → SensorLocationType the sensor
// event lacks.
export function buildLocationTypeMap(layout: VehicleSensorLayout | null): Map<string, SensorLocationType> | null {
  const grid = layout?.grid;
  if (!grid) return null;
  const map = new Map<string, SensorLocationType>();
  for (const key of Object.keys(grid)) {
    map.set(key, normalizeLocationType(grid[key]?.type));
  }
  return map;
}

export function tireLocationsOf(locationTypes: Map<string, SensorLocationType> | null): string[] {
  return [...(locationTypes?.entries() ?? [])]
    .filter(([, type]) => isTireLocationType(type))
    .map(([location]) => location);
}

export interface SensorTile {
  location: string;
  sensor: SensorEvent | null;
  reading: SensorValue | null;
  row: number;
  col: number;
  // Spans grid rows — the truck's width axis is vertical in the rotated view.
  span: number;
  sevClass: string;
  stale: boolean;
}

// Tire lanes are [L|R][O|I][row] — axle (RA/LA), engine (C), and custom
// locations don't count toward the wheelbase columns.
const TIRE_LOCATION_CODE = /^[LR][OI]\d+$/i;

const MIN_WHEELBASE_COLUMNS = 6;

export interface SensorGridLayout {
  tiles: SensorTile[];
  rows: number;
  rowTemplate: string;
  colTemplate: string;
}

// Side view, rotated 90° CCW from the canonical top-down mapping: front is the
// left column, and canonical position[1] runs opposite the top-down view, so
// right-side sensors land in the top rows with no mirroring needed.
// emptyLocations render as unpopulated cells so a wheel position still shows
// without a sensor fitted.
export function buildSensorGrid(
  sensors: SensorEvent[],
  mapping: LocationPosition[],
  emptyLocations: string[] = [],
): SensorGridLayout {
  const byLocation = new Map(mapping.map(m => [m.location, m]));
  const sensorLocations = new Set(sensors.map(s => s.location));
  const cells: { location: string; sensor: SensorEvent | null }[] = [
    ...sensors.map(sensor => ({ location: sensor.location, sensor })),
    ...emptyLocations.filter(l => !sensorLocations.has(l)).map(location => ({ location, sensor: null })),
  ];

  const placed = cells
    .map(cell => ({ ...cell, pos: byLocation.get(cell.location) }))
    .filter((p): p is { location: string; sensor: SensorEvent | null; pos: LocationPosition } => !!p.pos?.position);

  if (!placed.length) return { tiles: [], rows: 1, rowTemplate: '', colTemplate: '' };

  // Equal columns, one per axle slot — like the legacy grid, a tire sits in
  // the column its code names (RO5 → column 5). The canonical mapping defines
  // up to nine axle rows, but a standard wheelbase reads as six columns, so
  // slots past six only render when a tire actually occupies them. Only
  // tire-coded rows anchor the axis: other mapped locations (doors, kingpin)
  // can sit at far row positions and would dilute the columns.
  const tireRows = mapping.filter(m => !m.isFullWidth && m.position && TIRE_LOCATION_CODE.test(m.location));
  const minAxle = Math.min(...(tireRows.length ? tireRows : placed.map(p => p.pos)).map(m => m.position[0]));
  const usedCols = placed.filter(p => !p.pos.isFullWidth).map(p => p.pos.position[0] - minAxle + 1);
  const colCount = Math.max(MIN_WHEELBASE_COLUMNS, ...(usedCols.length ? usedCols : [1]));
  const colTemplate = `repeat(${colCount}, minmax(0, 1fr))`;
  const colOf = (axle: number) => Math.min(Math.max(axle - minAxle + 1, 1), colCount);
  // A flexible spacer track between bands pools the bed's free height in the
  // middle, so dual tires sit snug while the bands hug the bed's edges.
  const bands = tireBands(placed.filter(p => !p.pos.isFullWidth).map(p => p.pos));
  const rows = (bands.reduce((n, b) => n + b.width, 0) + Math.max(0, bands.length - 1)) || 1;
  const rowTemplate = bands.map(b => `repeat(${b.width}, auto)`).join(' 1fr ');

  const tiles = placed.map(({ location, sensor, pos }) => {
    const { row, span } = pos.isFullWidth ? { row: 1, span: rows } : bandPlacement(bands, pos);
    return {
      location,
      sensor,
      reading: sensor ? pressureSensorValue(sensor) : null,
      row,
      col: colOf(pos.position[0]),
      span,
      sevClass: sensor ? sensorSeverityCss(sensor) : 'sev-empty',
      stale: sensor ? isSensorStale(sensor) : false,
    };
  });

  return { tiles, rows, rowTemplate, colTemplate };
}

// The tenant grid interleaves non-tire lanes (axles, engine) between the tire
// lanes, so raw lane indices can't be grid rows — a 7-lane grid would render 5
// empty tracks. Instead the tires collapse into bands: connected lane intervals
// (right side, then left), each as wide as its widest axle. A lone tire on an
// axle fills its band, so outer-only axles line up with the steer tires.
interface TireBand {
  lanes: number[];
  start: number;
  width: number;
  members: LocationPosition[];
}

function tireBands(tires: LocationPosition[]): TireBand[] {
  const sorted = [...tires].sort((a, b) => a.position[1] - b.position[1]);
  const groups: LocationPosition[][] = [];
  let end = -Infinity;
  for (const t of sorted) {
    // Adjacent lanes are one band (RO5/RI5 duals sit in neighboring lanes and
    // never overlap); only a lane skip — the axle/engine lanes between the
    // sides — starts a new band.
    if (t.position[1] > end + 1) groups.push([]);
    groups[groups.length - 1].push(t);
    end = Math.max(end, t.position[1] + (t.colSpan || 1) - 1);
  }
  let start = 1;
  return groups.map(members => {
    const lanes = [...new Set(members.map(m => m.position[1]))].sort((a, b) => a - b);
    const perAxle = new Map<number, number>();
    members.forEach(m => perAxle.set(m.position[0], (perAxle.get(m.position[0]) ?? 0) + 1));
    const width = Math.max(...perAxle.values());
    const band = { lanes, start, width, members };
    start += width + 1; // +1 skips the spacer track between bands
    return band;
  });
}

function bandPlacement(bands: TireBand[], pos: LocationPosition): { row: number; span: number } {
  const band = bands.find(b => b.members.includes(pos));
  if (!band) return { row: 1, span: 1 };
  const tilesOnAxle = band.members.filter(m => m.position[0] === pos.position[0]).length;
  if (tilesOnAxle === 1 && !/^[LR]I/i.test(pos.location)) {
    // A lone outer tire on an axle hugs the truck's edge — the band's outer row —
    // never straddling the dual lanes. Inner-coded lone tires keep their lane rank.
    return { row: band === bands[0] ? band.start : band.start + band.width - 1, span: 1 };
  }
  const rank = Math.min(band.lanes.indexOf(pos.position[1]), band.width - 1);
  return { row: band.start + rank, span: 1 };
}
